import { Component } from '@theme/component';

/**
 * The cookie banner, matched to Nour Hammour's own (DECISIONS 13 Sept 2026, measured live that
 * day): a small card, centred on the screen, never modal, on any layer including Preferences.
 * One native <dialog>, always opened with show(), never showModal(): the page stays fully live
 * and readable behind it throughout, exactly as a visitor of Nour Hammour's own site experiences
 * it, and there is no scrim to draw or background to make inert. Consent goes to Shopify's
 * Customer Privacy API. A group the merchant does not offer is never set to true, not even by
 * Accept all.
 *
 * The close button and Escape both leave without recording a choice, on any layer, matching Nour
 * Hammour's own X (measured: it changes no Shopify consent cookie). Since nothing here ever
 * blocks the page, that dismissal is remembered for the rest of the browser session
 * (sessionStorage), or the banner would return on every single page view; a new session is asked
 * again, the same as theirs.
 *
 * Who sees it does not depend on Shopify's admin regions, which the documentation does not
 * describe once a custom banner is chosen: every visitor with no recorded answer (Matt, 13 Sept
 * 2026: everywhere), or only the countries listed in Theme settings.
 */

const GROUPS = ['preferences', 'analytics', 'marketing'];
const OPEN_LINK = 'a[href$="#cookie-preferences"]';
const DISMISSED_KEY = 'consent-banner-dismissed';

/** @returns {Promise<any>} Shopify's customerPrivacy object, loaded on demand. */
function privacyApi() {
  return new Promise((resolve, reject) => {
    const ready = () => window.Shopify?.customerPrivacy;
    if (ready()) return resolve(ready());
    if (!window.Shopify?.loadFeatures) return reject(new Error('Shopify.loadFeatures is not available'));
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (/** @type {any} */ error) => {
      if (error) reject(error);
      else resolve(ready());
    });
  });
}

/**
 * @typedef {object} ConsentRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} body
 * @property {HTMLButtonElement} prefsToggle
 * @property {HTMLElement} groups
 * @property {HTMLElement} firstActs
 * @property {HTMLButtonElement} reject
 * @property {HTMLButtonElement} accept
 * @property {HTMLElement} prefsActs
 * @property {HTMLButtonElement} acceptPrefs
 * @property {HTMLButtonElement} save
 * @property {HTMLButtonElement} close
 * @property {HTMLElement} status
 */

/** @extends {Component<ConsentRefs>} */
class ConsentBanner extends Component {
  requiredRefs = [
    'dialog',
    'body',
    'prefsToggle',
    'groups',
    'firstActs',
    'reject',
    'accept',
    'prefsActs',
    'acceptPrefs',
    'save',
    'close',
    'status',
  ];

  /** @type {'first' | 'prefs' | 'reopen' | null} */
  #layer = null;

  /** @type {HTMLElement | null} */
  #returnFocus = null;

  /** @type {any} */
  #api = null;

  #abort = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abort;
    const { dialog, prefsToggle, reject, accept, acceptPrefs, save, close } = this.refs;

    prefsToggle.addEventListener('click', () => (this.#layer === 'prefs' ? this.#collapse() : this.#open('prefs')), {
      signal,
    });
    reject.addEventListener('click', () => this.#choose(false), { signal });
    accept.addEventListener('click', () => this.#choose(true), { signal });
    acceptPrefs.addEventListener('click', () => this.#choose(true), { signal });
    save.addEventListener('click', () => this.#choose(null), { signal });
    close.addEventListener('click', () => this.#dismiss(), { signal });

    // Nothing here is modal, so the browser's own Escape-to-close never fires (that belongs to
    // showModal()). Offered anyway, on any layer, since a visitor who can reach for Escape expects
    // it to work; it leaves without recording a choice, the same as the close button.
    dialog.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape') this.#dismiss();
      },
      { signal }
    );

    // Cookie preferences in the footer is a menu item linking to #cookie-preferences.
    document.addEventListener(
      'click',
      (event) => {
        const link = event.target instanceof Element ? event.target.closest(OPEN_LINK) : null;
        if (!(link instanceof HTMLElement)) return;
        event.preventDefault();
        this.#returnFocus = link;
        this.#open('reopen');
      },
      { signal }
    );
    for (const link of document.querySelectorAll(OPEN_LINK)) link.setAttribute('aria-haspopup', 'dialog');

    this.#start();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort.abort();
  }

  get #inEditor() {
    return this.dataset.editor !== undefined;
  }

  async #start() {
    // Theme editor: show what Theme settings > Cookie banner asks for, so a change shows as it is made.
    if (this.#inEditor) {
      if (this.dataset.editor !== 'hidden') this.#open(this.dataset.editor === 'prefs' ? 'prefs' : 'first');
      return;
    }

    const preview = new URLSearchParams(window.location.search).has('consent_preview');
    try {
      this.#api = await privacyApi();
    } catch (error) {
      console.warn('[consent-banner] Customer Privacy API unavailable', error);
      if (preview) this.#open('first');
      return;
    }

    const answered = this.#answered();
    const dismissed = this.#dismissedThisSession();
    const inRegion = this.dataset.regions !== 'list' || this.#countries().includes(this.dataset.country ?? '');

    if (preview) {
      // Our rule beside Shopify's own, logged on a preview only (the build plan's region check).
      console.info('[consent-banner]', {
        ours: inRegion && !answered && !dismissed,
        shopify: this.#api.shouldShowBanner?.(),
        country: this.dataset.country,
      });
      this.#open('first');
      return;
    }

    if (this.dataset.enabled === 'true' && inRegion && !answered && !dismissed) this.#open('first');
  }

  /** Closing without choosing (the X, or Escape) is remembered for this browser session only, the
   *  same as Nour Hammour's own banner (measured 13 Sept 2026: it sets a session cookie, not its
   *  real consent record). Without this, nothing here ever blocks the page, so the banner would
   *  return on every single page view rather than just the next new session. */
  #dismissedThisSession() {
    try {
      return sessionStorage.getItem(DISMISSED_KEY) === '1';
    } catch {
      return false; // Storage can throw in a locked-down context; fail open to showing the banner.
    }
  }

  /** @returns {string[]} */
  #countries() {
    return (this.dataset.countries ?? '').split(',').filter(Boolean);
  }

  #answered() {
    const consent = this.#api?.currentVisitorConsent?.() ?? {};
    return GROUPS.some((group) => consent[group] === 'yes' || consent[group] === 'no');
  }

  /** @returns {HTMLInputElement[]} */
  #switches() {
    return [...this.refs.groups.querySelectorAll('input[role="switch"]')].filter(
      (input) => input instanceof HTMLInputElement
    );
  }

  /** @param {'first' | 'prefs' | 'reopen'} layer */
  #open(layer) {
    const { dialog, groups, prefsToggle, firstActs, prefsActs } = this.refs;
    const expanded = layer !== 'first';

    this.#layer = layer;
    dialog.dataset.layer = layer;
    groups.hidden = !expanded;
    prefsActs.hidden = !expanded;
    firstActs.hidden = expanded;
    prefsToggle.setAttribute('aria-expanded', String(expanded));

    if (layer === 'reopen') {
      const consent = this.#api?.currentVisitorConsent?.() ?? {};
      for (const input of this.#switches()) input.checked = consent[input.name] === 'yes';
    }

    // Always show(), never showModal(): no layer ever blocks the page (Nour Hammour parity, 13
    // Sept 2026). show() still runs the dialog's own focusing steps in some browsers, so the first
    // layer's focus is put back where it was rather than left on the title (found on the preview
    // theme, 13 Sept 2026, before this banner blocked anything at all).
    if (!dialog.open) {
      const before = document.activeElement;
      dialog.show();
      if (before instanceof HTMLElement && before !== document.body) before.focus({ preventScroll: true });
      else if (document.activeElement instanceof HTMLElement && dialog.contains(document.activeElement)) document.activeElement.blur();
    }
  }

  #collapse() {
    this.#open('first');
    this.refs.prefsToggle.focus();
  }

  /** Leaves without recording a choice: the close button, or Escape, on any layer. */
  #dismiss() {
    const { dialog } = this.refs;
    this.#layer = null;
    if (dialog.open) dialog.close();
    try {
      sessionStorage.setItem(DISMISSED_KEY, '1');
    } catch {
      // Storage can throw in a locked-down context; the banner simply returns next page view.
    }

    // Focus goes back where the visitor came from: the footer link after a reopen, and the next
    // stop after the banner, the skip link, after a first visit.
    const next = this.#returnFocus ?? document.querySelector('.skip-to-content-link');
    if (next instanceof HTMLElement) next.focus();
    this.#returnFocus = null;
  }

  /** @param {boolean | null} all - true accepts every offered group, false rejects every group, null saves the switches */
  async #choose(all) {
    const switches = this.#switches();
    const offered = new Set(switches.map((input) => input.name));
    /** @type {Record<string, boolean>} */
    const choice = {};
    for (const group of GROUPS) {
      if (!offered.has(group)) choice[group] = false;
      else if (all !== null) choice[group] = all;
      else choice[group] = switches.find((input) => input.name === group)?.checked ?? false;
    }

    if (!this.#inEditor) {
      try {
        const api = this.#api ?? (await privacyApi());
        await new Promise((resolve, reject) =>
          api.setTrackingConsent(choice, (/** @type {any} */ result) =>
            result?.error ? reject(result.error) : resolve(result)
          )
        );
      } catch (error) {
        // A choice that was not recorded is not a choice: the card stays.
        console.error('[consent-banner] consent not recorded', error);
        return;
      }
    }

    this.refs.status.textContent = this.refs.status.dataset.saved ?? '';
    this.#dismiss();
  }
}

if (!customElements.get('consent-banner')) customElements.define('consent-banner', ConsentBanner);
